/**
 * Modal de detalhes do serviço com imagem ampliada e descrição completa.
 */

import { useEffect, useState } from "react";
import type { PublicService } from "../types";
import { resolveImageUrl } from "../utils";
import { formatDurationMinutes } from "../../../lib/time";
import { FiCheck, FiClock, FiImage, FiTag, FiX } from "react-icons/fi";

interface ServiceDetailsModalProps {
  service: PublicService | null;
  priceLabel: string;
  selected: boolean;
  onToggleSelect: () => void;
  onClose: () => void;
}

export function ServiceDetailsModal({
  service,
  priceLabel,
  selected,
  onToggleSelect,
  onClose,
}: ServiceDetailsModalProps) {
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    setImageFailed(false);
  }, [service?.id]);

  useEffect(() => {
    if (!service) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose, service]);

  if (!service) return null;

  const imageSrc = service.imageUrl ? resolveImageUrl(service.imageUrl) : "";
  const showImage = Boolean(imageSrc) && !imageFailed;

  return (
    <div
      className="fixed inset-0 z-[70] flex animate-backdrop-in items-end justify-center bg-slate-950/70 backdrop-blur-sm motion-reduce:animate-none sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`Detalhes do serviço ${service.name}`}
      onClick={onClose}
    >
      <div
        className="flex max-h-[92dvh] w-full animate-sheet-up flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl will-change-transform motion-reduce:animate-none sm:max-w-xl sm:animate-dialog-in sm:rounded-3xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="relative aspect-[4/3] w-full shrink-0 overflow-hidden bg-slate-900 sm:aspect-[16/10]">
          {showImage ? (
            <>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={imageSrc}
                alt=""
                aria-hidden="true"
                className="absolute inset-0 h-full w-full scale-125 object-cover opacity-45 blur-2xl"
              />
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={imageSrc}
                alt={service.name}
                className="relative z-10 h-full w-full object-contain"
                onError={() => setImageFailed(true)}
              />
            </>
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-slate-100 text-slate-400">
              <FiImage className="h-10 w-10" aria-hidden="true" />
            </div>
          )}

          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar detalhes"
            className="absolute right-3 top-3 z-20 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/95 text-slate-700 shadow-lg ring-1 ring-slate-900/5 transition hover:bg-white focus:outline-none focus-visible:ring-4 focus-visible:ring-primary-100"
          >
            <FiX className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
          <h2 className="text-2xl font-bold leading-tight text-[#233055]">
            {service.name}
          </h2>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1.5 text-sm font-semibold text-slate-700">
              <FiClock className="h-4 w-4" aria-hidden="true" />
              {formatDurationMinutes(service.durationMinutes)}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-50 px-3 py-1.5 text-sm font-semibold text-primary-700">
              <FiTag className="h-4 w-4" aria-hidden="true" />
              {priceLabel}
            </span>
          </div>

          <div className="mt-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Sobre o serviço
            </p>
            <p className="mt-1.5 whitespace-pre-line text-sm leading-6 text-slate-600">
              {service.description?.trim()
                ? service.description
                : "Este serviço ainda não tem uma descrição detalhada. Fale com o estabelecimento se tiver dúvidas."}
            </p>
          </div>
        </div>

        <div className="shrink-0 border-t border-slate-200 bg-white px-5 py-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
          <button
            type="button"
            onClick={onToggleSelect}
            className={`inline-flex min-h-[3rem] w-full items-center justify-center gap-2 rounded-2xl px-6 py-3 text-sm font-bold leading-none transition focus:outline-none focus-visible:ring-4 ${
              selected
                ? "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 focus-visible:ring-slate-100"
                : "bg-gradient-to-r from-primary-600 to-primary-700 text-white shadow-lg shadow-primary-600/25 hover:from-primary-700 hover:to-primary-800 focus-visible:ring-primary-100"
            }`}
          >
            {selected ? (
              <>
                <FiCheck className="h-4 w-4" aria-hidden="true" />
                Serviço selecionado · remover
              </>
            ) : (
              "Selecionar serviço"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
